import { textToItems } from '@/utils/format';

const BULLET = /^\s*(?:[-*+•●▪–]|\d+[.)])\s+/;
const HEADING = /^\s*#{1,6}\s+/;

/**
 * Free text from a listing as paragraphs, sub-headings and bullet lists.
 *
 * Companies type into a plain textarea: a line starting with "-", "*", "•" or
 * "1." is a bullet, "## Heading" a sub-heading, a blank line ends a paragraph.
 * Markdown emphasis and links are reduced to their text, and nothing is ever
 * rendered as HTML -- the text reaches the page only as React text nodes.
 */
function toBlocks(value) {
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'p', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushList = () => {
    if (list?.length) blocks.push({ type: 'ul', items: list });
    list = null;
  };

  for (const raw of value.split(/\r?\n/)) {
    const [text] = textToItems(raw);
    if (!text) {
      flushParagraph();
      flushList();
    } else if (HEADING.test(raw)) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h', text });
    } else if (BULLET.test(raw)) {
      flushParagraph();
      list = list ?? [];
      list.push(text);
    } else {
      flushList();
      paragraph.push(text);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Prose: paragraphs, with bullets and sub-headings where the text has them. */
export function ListingProse({ text }) {
  if (!text) return null;
  return (
    <div className="text-ink/85 space-y-3 text-sm leading-relaxed">
      {toBlocks(text).map((block, index) => {
        if (block.type === 'h') {
          return (
            <h4 key={index} className="text-ink pt-1 font-semibold">
              {block.text}
            </h4>
          );
        }
        if (block.type === 'ul') return <BulletList key={index} items={block.items} />;
        return <p key={index}>{block.text}</p>;
      })}
    </div>
  );
}

/**
 * A section written one item per line (responsibilities, requirements,
 * perks) as a bullet list; a single line stays a sentence.
 */
export function ListingItems({ text }) {
  const items = textToItems(text);
  if (items.length === 0) return null;
  if (items.length === 1) {
    return <p className="text-ink/85 text-sm leading-relaxed">{items[0]}</p>;
  }
  return <BulletList items={items} />;
}

function BulletList({ items }) {
  return (
    <ul className="text-ink/85 space-y-1.5 text-sm leading-relaxed">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2.5">
          <span
            aria-hidden="true"
            className="bg-ink-muted/60 mt-[0.6em] size-1 shrink-0 rounded-full"
          />
          <span className="min-w-0 break-words">{item}</span>
        </li>
      ))}
    </ul>
  );
}
